import { useState, useCallback, useMemo } from 'react';
import { useAuthenticatedWebSocket } from '../hooks/useAuthenticatedWebSocket';
import { useToastNotifications, ToastContainer } from './ToastNotification';
import { shouldFilterMessage, isSessionCompleteMessage, logFilteredMessage } from '../utils/messageFilter';
import { Clock, WifiOff, Info, Shield } from 'lucide-react';

type Props = {
  chargerId?: string;
  onUpdate?: () => void;
  onConnectionChange?: (isConnected: boolean) => void;
};

export const ChargerLiveUpdates = ({ chargerId, onUpdate, onConnectionChange }: Props) => {
  const [notifications, setNotifications] = useState<{ message: string; timestamp: Date }[]>([]);
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');
  const [refreshLogs, setRefreshLogs] = useState<{ message: string; timestamp: Date; type: 'info' | 'success' | 'error' }[]>([]);
  
  // Toast notification hooks for real-time visual feedback
  const {
    notifications: toastNotifications,
    addSuccessNotification,
    addOverdueReminder,
    addQueueUpdate,
    removeNotification: removeToastNotification
  } = useToastNotifications();
  
  // Helper function to add a refresh log - memoized to prevent re-renders
  const addRefreshLog = useCallback((message: string, type: 'info' | 'success' | 'error' = 'info') => {
    setRefreshLogs(prev => [
      { message, timestamp: new Date(), type },
      ...prev.slice(0, 4) // Keep only 5 most recent refresh logs
    ]);
  }, []);
  
  // Helper function to add a notification - memoized to prevent re-renders
  const addNotification = useCallback((message: string) => {
    // Safety filter using centralized message filter utility
    if (isSessionCompleteMessage({ data: { message } })) {
      logFilteredMessage({ data: { message } }, 'Session completed notification blocked in addNotification');
      return;
    }
    
    setNotifications(prev => [
      { message, timestamp: new Date() },
      ...prev.slice(0, 9) // Keep only 10 most recent notifications
    ]);
  }, []);
  
  // Handle incoming WebSocket messages - memoized to prevent re-renders
  const handleMessage = useCallback((message: any) => {
    console.log('Received WebSocket message:', message);
    
    // Comprehensive message filtering using centralized utility
    if (shouldFilterMessage(message)) {
      logFilteredMessage(message, 'Message blocked by centralized filter');
      return; // Exit early - don't process this message at all
    }
    
    // Additional specific check for session complete messages (double safety)
    if (isSessionCompleteMessage(message)) {
      logFilteredMessage(message, 'Session complete message blocked');
      return;
    }
    
    // Handle different message types with appropriate toasts
    switch (message.type) {
      case 'ChargerUpdate':
        addNotification(`Charger update: Status changed to ${message.data.status}`);
        addSuccessNotification(`🔌 Charger Status Update`, {
          status: message.data.status,
          chargerId: message.data.chargerId
        });
        if (onUpdate) onUpdate(); // Trigger update callback
        break;
      case 'QueueUpdate':
        addNotification(`Queue update: ${message.data.queueLength} users in queue`);
        addQueueUpdate(`📋 Queue Update: ${message.data.queueLength} users in queue`, {
          queueLength: message.data.queueLength,
          chargerId: message.data.chargerId
        });
        if (onUpdate) onUpdate(); // Trigger update callback
        break;
      case 'Broadcast':
        addNotification(`Broadcast: ${message.data.message || JSON.stringify(message.data)}`);
        addSuccessNotification(`📢 Broadcast`, {
          message: message.data.message || 'System announcement'
        });
        if (onUpdate) onUpdate(); // Trigger update callback
        break;
      case 'Connected':
        addNotification(`Connected to charger ${message.data.chargerId || 'broadcast channel'}`);
        // No toast for connection messages - these are system messages
        break;
      case 'Subscribed':
        addNotification(`Subscribed to charger ${message.data.ChargerId || message.data.chargerId || 'updates'}`);
        // No toast for subscription confirmations - these are system messages
        break;
      case 'AuthenticatedConnected':
        // Connection established silently - no notification needed
        break;
      case 'ChargerSubscribed':
        addNotification(`Subscribed to charger ${message.data.ChargerId || 'updates'}`);
        // No toast for subscription confirmations - these are system messages
        break;
      case 'ChargerUnsubscribed':
        addNotification(`Unsubscribed from charger ${message.data.ChargerId || 'updates'}`);
        // No toast for unsubscription confirmations - these are system messages
        break;
      case 'PublicBroadcastSubscribed':
        addNotification(`Subscribed to public announcements`);
        // No toast for subscription confirmations - these are system messages
        break;
      case 'NotificationBroadcast':
        addNotification(`Broadcast: ${message.data.message || JSON.stringify(message.data)}`);
        // Only show toast for important broadcasts, not routine system announcements
        if (message.data.Priority === 'High' || message.data.Important === true) {
          addSuccessNotification(`📢 Important Announcement`, {
            message: message.data.message || 'System announcement'
          });
        }
        if (onUpdate) onUpdate(); // Trigger update callback
        break;
      case 'UserNotification':
        // Handle user-specific notifications with enhanced toast display
        console.log('📨 UserNotification received - processing for toast display');
        console.log('� Message data:', message.data);
        
        const notificationType = message.data.Metadata?.NotificationType || message.data.Type || message.data.type || 'General';
        const messageContent = message.data.Message || message.data.message || 'Notification received';
        
        // Note: Session Completed messages are already filtered out by global filter above
        
        // Show appropriate toast based on notification type
        if (notificationType === 'OverdueReminder' || messageContent.includes('overdue') || messageContent.includes('Overdue')) {
          addOverdueReminder(messageContent, {
            notificationType,
            telegramId: message.data.TelegramId,
            sessionId: message.data.SessionId,
            chargerId: message.data.ChargerId
          });
        } else if (notificationType === 'QueueUpdate') {
          addQueueUpdate(messageContent, {
            notificationType,
            telegramId: message.data.TelegramId
          });
        } else {
          // General notification
          addSuccessNotification(`🔔 ${messageContent}`, {
            notificationType,
            telegramId: message.data.TelegramId
          });
        }
        
        addNotification(`🔔 Personal notification: ${messageContent}`);
        if (onUpdate) onUpdate();
        break;
      case 'SessionUpdate':
        const sessionStatus = message.data.status || JSON.stringify(message.data);
        
        // Use centralized filtering for session completion notifications
        if (isSessionCompleteMessage(message)) {
          logFilteredMessage(message, 'Session completion toast notification blocked');
        } else {
          addSuccessNotification(`⚡ Session Update`, {
            status: message.data.status,
            sessionId: message.data.sessionId
          });
        }
        
        addNotification(`⚡ Session update: ${sessionStatus}`);
        if (onUpdate) onUpdate();
        break;
      case 'RefreshChargersData':
        // Handle chargers data refresh signal - just trigger refresh immediately
        console.log('🔄 RefreshChargersData message received - triggering immediate refresh');
        
        addNotification(`🔄 Update - Refreshing chargers data`);
        addRefreshLog(`📨 SignalR Refresh Signal Received`, 'info');
        
        // Always trigger refresh when we receive this message - keep it simple!
        if (onUpdate) {
          console.log(`🔄 Calling onUpdate() to refresh data`);
          addRefreshLog(`🔄 Calling onUpdate() to refresh data`, 'info');
          
          try {
            onUpdate();
            console.log(`🔄 onUpdate() called successfully - data should be refreshing`);
            addRefreshLog(`✅ onUpdate() executed successfully - data refreshing`, 'success');
          } catch (error) {
            console.error(`🔄 onUpdate() failed:`, error);
            addRefreshLog(`❌ onUpdate() failed: ${error}`, 'error');
          }
        } else {
          console.error('🔄 onUpdate callback is not available!');
          addRefreshLog(`❌ onUpdate callback not available!`, 'error');
        }
        
        addSuccessNotification(`🔄 Data Refreshed`, {
          message: 'Charger data refreshed via SignalR'
        });
        break;
      case 'Error':
        addNotification(`❌ Error: ${message.data}`);
        break;
      default:
        // For any other message, just log it
        addNotification(`Received: ${message.type}`);
    }
  }, [addNotification, onUpdate, chargerId, addOverdueReminder, addQueueUpdate, addSuccessNotification]);

  // Function to update the LED indicator in the header
  const updateLedIndicator = useCallback((connected: boolean) => {
    const ledElement = document.getElementById('connection-led')?.querySelector('.led-dot');
    if (ledElement) {
      ledElement.className = `led-dot ${connected ? 'connected' : 'disconnected'}`;
    }
    
    // Call the parent callback if provided
    if (onConnectionChange) {
      onConnectionChange(connected);
    }
  }, [onConnectionChange]);

  // WebSocket event handlers - memoized to prevent re-renders
  const webSocketOptions = useMemo(() => ({
    chargerId,
    onOpen: () => {
      setConnectionStatus('Connected');
      updateLedIndicator(true);
      // WebSocket connected silently - no notification needed
    },
    onMessage: handleMessage,
    onError: () => {
      setConnectionStatus('Error connecting');
      updateLedIndicator(false);
      // Connection error handled silently - no notification needed
    },
    onClose: () => {
      setConnectionStatus('Disconnected');
      updateLedIndicator(false);
      // Connection closed silently - no notification needed
    },
    onAuthError: (error: string) => {
      updateLedIndicator(false);
      addNotification(`🔒 Auth error: ${error}`);
    }
  }), [chargerId, handleMessage, addNotification, updateLedIndicator]);
  
  // Use authenticated WebSocket hook
  const { 
    isConnected, 
    connectionError,
    subscribeToCharger
  } = useAuthenticatedWebSocket(webSocketOptions);

  return (
    <div className="charger-live-updates">
      <div className="live-updates-header">
        <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? (
            <>
              <Shield size={16} />
              <span>
                Authenticated Live Updates
              </span>
            </>
          ) : (
            <>
              <WifiOff size={16} />
              <span>Disconnected</span>
            </>
          )}
        </div>
        <div className="charger-id">
          {chargerId ? `Charger: ${chargerId}` : 'Broadcast Channel'}
        </div>
      </div>
      
      {/* Connection Type and Authentication Status */}
      <div className="connection-info">
        <div className="connection-type">
          <span className="auth-badge authenticated">
            <Shield size={12} />
            Authenticated Connection
          </span>
        </div>
        {connectionError && (
          <div className="connection-error">
            ⚠️ {connectionError}
          </div>
        )}
      </div>
      
      {/* Connection Type and Authentication Status */}
      <div className="connection-info">
        <div className="connection-type">
          <span className="auth-badge authenticated">
            <Shield size={12} />
            Authenticated Connection
          </span>
        </div>
        {connectionError && (
          <div className="connection-error">
            ⚠️ {connectionError}
          </div>
        )}
      </div>
      
      <div className="notifications-container">
        <h3 className="notifications-title">
          <Info size={16} />
          Live Notifications
        </h3>
        
        {notifications.length === 0 ? (
          <div className="empty-notifications">
            <Clock size={24} />
            <p>Waiting for updates...</p>
          </div>
        ) : (
          <ul className="notifications-list">
            {notifications.map((notification, index) => (
              <li key={index} className="notification-item">
                <div className="notification-time">
                  {notification.timestamp.toLocaleTimeString()}
                </div>
                <div className="notification-message">
                  {notification.message}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {/* Refresh Activity Logs */}
      <div className="refresh-logs-container">
        <h3 className="refresh-logs-title">
          🔄 Refresh Activity
        </h3>
        
        {refreshLogs.length === 0 ? (
          <div className="empty-refresh-logs">
            <p>No refresh activity yet...</p>
          </div>
        ) : (
          <ul className="refresh-logs-list">
            {refreshLogs.map((log, index) => (
              <li key={index} className={`refresh-log-item ${log.type}`}>
                <div className="log-time">
                  {log.timestamp.toLocaleTimeString()}
                </div>
                <div className="log-message">
                  {log.message}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      
      <div className="live-updates-footer">
        <div className="status-indicator">
          <div className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></div>
          <span>{connectionStatus}</span>
        </div>
        
        {/* Authentication Actions */}
        {isConnected && chargerId && (
          <div className="auth-actions">
            <button 
              onClick={() => chargerId && subscribeToCharger(chargerId)}
              className="action-btn subscribe-btn"
            >
              📡 Subscribe to Updates
            </button>
          </div>
        )}
      </div>

      <style>{`
        .charger-live-updates {
          margin-top: 1rem;
          border: 1px solid var(--tg-theme-hint-color);
          border-radius: 12px;
          overflow: hidden;
          background: var(--tg-theme-secondary-bg-color, rgba(0,0,0,0.05));
        }
        
        .live-updates-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--tg-theme-hint-color);
        }
        
        .websocket-url {
          padding: 0.5rem 1rem;
          background: var(--tg-theme-bg-color);
          border-bottom: 1px solid var(--tg-theme-hint-color);
          font-family: monospace;
          font-size: 0.75rem;
          color: var(--tg-theme-hint-color);
          word-break: break-all;
        }
        
        .connection-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
        }
        
        .connection-status.connected {
          color: #4caf50;
        }
        
        .connection-status.disconnected {
          color: #f44336;
        }
        
        .connection-info {
          padding: 0.5rem 1rem;
          background: var(--tg-theme-bg-color);
          border-bottom: 1px solid var(--tg-theme-hint-color);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .auth-badge {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .auth-badge.authenticated {
          background: rgba(76, 175, 80, 0.1);
          color: #4caf50;
          border: 1px solid rgba(76, 175, 80, 0.3);
        }
        
        .auth-badge.anonymous {
          background: rgba(33, 150, 243, 0.1);
          color: #2196f3;
          border: 1px solid rgba(33, 150, 243, 0.3);
        }
        
        .connection-error {
          font-size: 0.75rem;
          color: #f44336;
          background: rgba(244, 67, 54, 0.1);
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
        }
        
        .auth-actions {
          display: flex;
          gap: 0.5rem;
        }
        
        .action-btn {
          padding: 0.25rem 0.5rem;
          border: none;
          border-radius: 6px;
          font-size: 0.75rem;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .subscribe-btn {
          background: var(--tg-theme-button-color);
          color: var(--tg-theme-button-text-color);
        }
        
        .subscribe-btn:hover {
          opacity: 0.8;
        }
        
        .notifications-container {
          padding: 0.75rem;
          min-height: 100px;
          max-height: 300px;
          overflow-y: auto;
        }
        
        .notifications-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0 0 0.75rem 0;
          font-size: 1rem;
          font-weight: 500;
        }
        
        .empty-notifications {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          color: var(--tg-theme-hint-color);
          text-align: center;
        }
        
        .notifications-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .notification-item {
          display: flex;
          margin-bottom: 0.5rem;
          padding: 0.5rem;
          background: var(--tg-theme-bg-color);
          border-radius: 6px;
          border-left: 3px solid var(--tg-theme-button-color);
        }
        
        .notification-time {
          font-size: 0.75rem;
          color: var(--tg-theme-hint-color);
          width: 70px;
          flex-shrink: 0;
        }
        
        .notification-message {
          flex-grow: 1;
          font-size: 0.875rem;
        }
        
        .refresh-logs-container {
          padding: 0.75rem;
          min-height: 80px;
          max-height: 200px;
          overflow-y: auto;
          border-top: 1px solid var(--tg-theme-hint-color);
          background: rgba(0, 123, 255, 0.05);
        }
        
        .refresh-logs-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0 0 0.75rem 0;
          font-size: 1rem;
          font-weight: 500;
          color: #007acc;
        }
        
        .empty-refresh-logs {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          color: var(--tg-theme-hint-color);
          text-align: center;
          font-style: italic;
        }
        
        .refresh-logs-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .refresh-log-item {
          display: flex;
          margin-bottom: 0.4rem;
          padding: 0.4rem;
          background: var(--tg-theme-bg-color);
          border-radius: 6px;
          font-size: 0.8rem;
        }
        
        .refresh-log-item.info {
          border-left: 3px solid #007acc;
        }
        
        .refresh-log-item.success {
          border-left: 3px solid #4caf50;
          background: rgba(76, 175, 80, 0.1);
        }
        
        .refresh-log-item.error {
          border-left: 3px solid #f44336;
          background: rgba(244, 67, 54, 0.1);
        }
        
        .log-time {
          font-size: 0.7rem;
          color: var(--tg-theme-hint-color);
          width: 70px;
          flex-shrink: 0;
        }
        
        .log-message {
          flex-grow: 1;
          font-weight: 500;
        }
        
        .live-updates-footer {
          padding: 0.75rem 1rem;
          border-top: 1px solid var(--tg-theme-hint-color);
          display: flex;
          justify-content: space-between;
        }
        
        .status-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
        }
        
        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
        
        .status-dot.connected {
          background-color: #4caf50;
          box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.3);
        }
        
        .status-dot.disconnected {
          background-color: #f44336;
          box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.3);
        }
      `}</style>
      
      {/* Toast Container for real-time notifications */}
      <ToastContainer 
        notifications={toastNotifications}
        onClose={removeToastNotification}
      />
    </div>
  );
};